import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
import { useDebouncedCallback } from "use-debounce";
//#endregion
//#region types
import { SearchInputProps } from "models/propTypes/wintFormPropsTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { FieldConstants, IconDefinitionConstants } from "utils/constants";
import { DialogEnums } from "models/enums";
//#endregion
//#region components
import Icon from "../../icon";
import { Grid, CircularProgress, Autocomplete } from "@mui/material";
import useAutoCompleteCommonProps from "./useAutoCompleteCommonProps";
import BaseInput from "./baseInput";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const SearchInput: React.FC<SearchInputProps> = (props) => {
  const itemNone: AutocompleteListItem = {
    value: UtilsHelper.getSelectionNoneText(),
  };

  const [inputValue, setInputValue] = React.useState(props.value?.value ?? "");
  const [isValueFromOptions, setIsValueFromOptions] = React.useState<boolean>(true);

  const [options, setOptions] = React.useState<AutocompleteListItem[]>();
  const [currentAction, setCurrentAction] = React.useState<AutocompleteListItem>();

  const [open, setOpen] = React.useState<boolean>(false);
  const [focused, setFocused] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const selectedItem: AutocompleteListItem | undefined = props.value?.value === undefined ? undefined : props.value;

  React.useEffect(() => {
    if (inputValue !== props.value?.value) setInputValue(props.value?.value ?? "");
  }, [props.value]);

  const onChange = (event: any, newValue: AutocompleteListItem | string | null) => {
    if (newValue !== null) {
      setOpen(false);
      // Do NOT reset `focused` here: MUI keeps the input DOM-focused after a
      // selection (no blurOnSelect), so clearing it would desync `focused`
      // from real focus and leave the field unable to reopen until a real blur.
      setLoading(false);
    }
    if (typeof newValue === "string") return;

    props.setValue(newValue ?? undefined, true);
    props.setTouched(true, false);
  };

  const debouncedSearch = useDebouncedCallback((value: string, reason: string) => {
    if (reason === "input")
      searchItems(value)
        .then((items) => setOptions(items))
        .finally(() => setLoading(false));
  }, FieldConstants.DebounceTime);

  const getSuggestedItems = () => {
    const suggestedItems = props.includeNone ? [itemNone, ...(props.suggestedItems ?? [])] : props.suggestedItems;

    return suggestedItems?.map((x) => ({
      ...x,
      group: x.group ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
    }));
  };

  const setDefaultOptions = () => {
    setLoading(true);
    searchItems("")
      .then((items) => {
        if (
          JSON.stringify(
            (items ?? []).map((a) => {
              return { id: a.id, val: a.value };
            })
          ) !==
            JSON.stringify(
              (options ?? []).map((a) => {
                return { id: a.id, val: a.value };
              })
            ) ||
          options === undefined
        ) {
          setOptions(items ?? []);
        }
      })
      .finally(() => setLoading(false));
  };

  const searchItems = async (value: string) => {
    let searchItems;

    if (value === "" && props.suggestedItems) {
      searchItems = getSuggestedItems();
    } else {
      if (props.items)
        if (typeof props.items === "function") {
          searchItems = await props.items(value, props.format ? UtilsHelper.applyMask(value, props.format) : undefined);
        } else {
          const items = props.includeNone ? [itemNone, ...(props.items ?? [])] : (props.items ?? []);
          searchItems = value
            ? items
                .map((x) => ({
                  index: x.value.toLowerCase().indexOf(value.toLowerCase()),
                  item: x,
                }))
                .filter((x) => x.index >= 0)
                .sort((a, b) => a.index - b.index)
                .map((x) => x.item)
            : items;
        }
    }

    return searchItems ?? [];
  };

  const onInputChange = (event: object, value: string, reason: string) => {
    setLoading(true);
    if (focused) {
      setOpen(true);
    } else {
      setOpen(false);
    }

    setIsValueFromOptions(reason === "reset");

    setInputValue(value);
    debouncedSearch(value, reason);

    if (props.freeSolo) {
      if (props.value?.value !== value) {
        props.setValue(
          {
            value: value,
            description: "",
            group: "",
            data: "",
          },
          true
        );
        props.setTouched(true, false);
      }
    }
  };

  const onOpen = () => {
    if (!focused) {
      setDefaultOptions();
    }
  };

  const onClose = () => {
    setOpen(false);
    props.onClose && props.onClose();
  };

  const onFocus = () => {
    setFocused(true);
    setOpen(true);
    props.onFocus && props.onFocus();
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    setOpen(false);
    setTimeout(() => {
      props.onBlur && props.onBlur(event);
    });
  };

  const onActionClicked = (
    option: AutocompleteListItem,
    inputValue: string,
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    if (!option.enablePropagationOnActionClick) {
      event.stopPropagation();
    }
    setLoading(true);
    setOpen(false);
    setFocused(false);
    setCurrentAction(option);

    if (option.form) {
      dispatch(
        displayDialog({
          isOpen: false,
          id: option.title ?? "autoCompleteActionDialog",
          title: option.title ?? "",
          subTitle: option.subtitle ?? "",
          titleIcon: option.formIcon,
          dialogType: DialogEnums.DialogType.Content,
          actions: [
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
            },
          ],
          children: option.form,
          onSubmit: (value: any) => {
            setOpen(false);
            setLoading(false);
            setCurrentAction(undefined);
            props.setValue(value as AutocompleteListItem, true);
            props.touched(true, false);
          },
          onClose: () => {
            setFocused(true);
            setCurrentAction(undefined);
            setLoading(false);
          },
          maxWidth: "sm",
        })
      );
    } else {
      option.onActionClicked &&
        option.onActionClicked(inputValue).then((res) => {
          setLoading(false);
          setCurrentAction(undefined);
          if (res) {
            option.onActionSuccess && option.onActionSuccess(res);
            if (option.actionSuccessMessage) {
              setTimeout(() => {
                setActionResult(res);
              }, 500);
            } else {
              setActionResult(res);
            }
          }
        });
    }
  };

  const setActionResult = (result: AutocompleteListItem) => {
    props.setValue(result, true);
    props.setTouched(true, false);
  };

  const onClick = () => {
    if (focused && !open) {
      setDefaultOptions();
      setOpen(true);
    }
  };

  const isOptionEqualToValue = React.useCallback(
    (option: AutocompleteListItem, value: AutocompleteListItem) =>
      option.id === value?.id && option.value === value?.value,
    []
  );

  const commonProps = useAutoCompleteCommonProps(props.disabled, props.hasError, undefined, "id", "value");

  return (
    <Autocomplete
      {...commonProps}
      id={`autoComplete-${props.name}`}
      freeSolo={props.freeSolo || props.hideDropdownIcon}
      options={(options ?? getSuggestedItems() ?? []).concat(props.actions ?? [])}
      getOptionLabel={(option) => (typeof option !== "string" ? `${option.value}` : "")}
      loading={options === undefined}
      getOptionDisabled={(option) =>
        !!option.disabled || (currentAction !== undefined && currentAction?.id === option.id)
      }
      value={selectedItem ?? null}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      onChange={onChange}
      onInputChange={onInputChange}
      disabled={props.disabled}
      blurOnSelect={props.blurOnSelect}
      renderOption={(elements, option, state) => {
        if (option.onActionClicked || option.form) {
          const selectedAction = currentAction && currentAction?.id === option.id;
          return (
            <li {...elements} data-testid={`autoCompleteOption-${option.id}`}>
              <Grid
                key={option.id}
                onClick={onActionClicked.bind(null, option, state.inputValue)}
                className={clsx(cssExports.actionItemContainer, option.className)}
              >
                <Grid container className={cssExports.actionTitleOuter}>
                  <Grid item>
                    {selectedAction && loading ? (
                      <CircularProgress size={20} className={cssExports.buttonProgressWhite} />
                    ) : selectedAction ? (
                      <span className={clsx(cssExports.actionIcon, cssExports.success)}>
                        <Icon icon={IconDefinitionConstants.WCheck} />
                      </span>
                    ) : option.icon ? (
                      <span className={cssExports.actionIcon}>
                        <Icon icon={option.icon} />
                      </span>
                    ) : (
                      <></>
                    )}
                  </Grid>
                  <Grid item xs>
                    <Grid item className={cssExports.actionTitle}>
                      {currentAction?.actionSuccessMessage && selectedAction && !loading
                        ? currentAction.actionSuccessMessage
                        : option.value.replace(FieldConstants.ValueTemplateText, state.inputValue)}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </li>
          );
        } else {
          return commonProps.renderOption(elements, option, state);
        }
      }}
      groupBy={(option) => (option.group === undefined ? "" : `${option.group}`)}
      filterOptions={(options) =>
        options.filter(
          (option) =>
            (!props.error || !option.hideWhenInvalid) &&
            (!option.hide || !option.hide(options, inputValue, isValueFromOptions))
        )
      }
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={(params) => (
        <BaseInput
          type="search"
          className={props.className}
          name={props.name}
          value={inputValue}
          icon={props.icon}
          label={props.label}
          setValue={() => {}}
          touched={props.touched}
          setTouched={props.setTouched}
          hasError={props.hasError}
          onChange={params.inputProps.onChange ?? (() => {})}
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          inputProps={{ ...params.inputProps, value: inputValue }}
          InputProps={params.InputProps}
          isLoading={(props.showLoading && loading) || props.isLoading}
          optional={props.optional}
          disabled={props.disabled}
          maxLength={props.maxLength}
        />
      )}
      data-testid={`${props.name}SearchInput`}
      {...(props.popperMinWidth
        ? {
            slotProps: {
              popper: {
                style: { maxWidth: 400, minWidth: props.popperMinWidth },
                placement: "bottom-start" as const,
              },
              paper: { style: { maxWidth: 400, minWidth: props.popperMinWidth } },
            },
          }
        : {})}
    />
  );
};

export default SearchInput;
